import React from "react";
import _ from "lodash";
import moment from "moment";
import Select from "react-select";
import Checkbox from "components/common/Checkbox";
import ModalWrapper from "components/common/ModalWrapper";
import ConfigDiffView from "components/common/ConfigDiffView";
import HighchartWrapper from "components/common/HighchartWrapper";
import { CHANGETYPE } from "helpers/Compare";
import { getDateTimeLabel } from "helpers/Chart";
import { getDateList } from "helpers/DateTransform";
import { inputClass, buttonActionClassGray } from "helpers/StyleClass";

import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

import {
  LOG_EXPAND_LENGTH,
  LOG_STATUS,
  LOG_STATUS_CHECK,
  ON_STATUS,
  OFF_STATUS,
  CHANGE_SUMMARY,
  CHANGE_SUMMARY_CHECK,
} from "./Constants";

class ChangeLogModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dateList: [],
      changeLogs: [], // change logs data including parsed key and value
      expandList: [], // list of expanded log ids, id is `${entityId}-${changeTimeInUTC}`
      searchedLogs: [],

      searchInput: "",
      isOnlyChanged: true,
      isNetTimezone: true,
      selectedSort: "changeTime-desc",
      sortOptions: [
        {
          label: "Updated At (Descending)",
          value: "changeTime-desc",
        },
        {
          label: "Updated At (Ascending)",
          value: "changeTime-asc",
        },
      ],
      selectedRange: 30,
      rangeOptions: [3, 7, 14, 21, 30].map((num) => ({
        label: `In ${num} days`,
        value: num,
      })),
      selectedKey: "All",
      selectedSummary: "All",
      chartOptions: null,
    };

    this.handleSearchInputChange = this.handleSearchInputChange.bind(this);
    this.handleSearchLogs = this.handleSearchLogs.bind(this);
    this.handleExpand = this.handleExpand.bind(this);
    this.handleChartOptions = this.handleChartOptions.bind(this);
    this.handleClickDateLabel = this.handleClickDateLabel.bind(this);
    this.handleScrollToDate = this.handleScrollToDate.bind(this);

    this._checkInRange = this._checkInRange.bind(this);
    this._getDateList = this._getDateList.bind(this);
    this._getStatusBgClass = this._getStatusBgClass.bind(this);
    this._getListLengthByStatus = this._getListLengthByStatus.bind(this);
    this._getListLengthBySummary = this._getListLengthBySummary.bind(this);
  }

  async componentDidMount() {
    const { changeLogs, logIds } = this.props;
    if (changeLogs && changeLogs.length > 0) {
      const filteredChangeLogs =
        logIds.length > 0
          ? _.filter(changeLogs, (log) => logIds.includes(log.entityId))
          : changeLogs;

      const parsedChangeLogs = _.orderBy(
        filteredChangeLogs,
        this.state.selectedSort.split("-")
      ).map((log) => {
        const timeUtc = _.toNumber(log.changeTimeInUTC);

        const changeDiff = this.parseChangeToDiff(log.changes);

        return {
          ...log,
          changeTimeLocal: moment(timeUtc).format("YYYY/MM/DD HH:mm:ss Z"),
          changeTimeInTz: moment
            .utc(timeUtc)
            .tz(this.props.timezone)
            .format("YYYY/MM/DD HH:mm:ss Z"),
          changeTime: timeUtc,
          id: `${log.entityId}-${timeUtc}`,
          changeDiff,
          subChangeDiff: log.subRules.map((subRule) => ({
            ...subRule,
            changes: this.parseChangeToDiff(subRule.changes),
          })),
          status: _.keys(LOG_STATUS_CHECK).filter((statusKey) => {
            return _.find(changeDiff, LOG_STATUS_CHECK[statusKey]);
          }),
          summary: _.keys(CHANGE_SUMMARY_CHECK).filter((contentKey) => {
            return _.find(changeDiff, CHANGE_SUMMARY_CHECK[contentKey]);
          }),
        };
      });

      this.setState(
        {
          changeLogs: parsedChangeLogs,
          searchedLogs: parsedChangeLogs,
          dateList: this._getDateList(),
          expandList:
            parsedChangeLogs.length > LOG_EXPAND_LENGTH
              ? []
              : parsedChangeLogs.map((log) => log.id),
        },
        () => {
          this.handleChartOptions();
        }
      );

      this.updateInputValue = _.debounce((value) => {
        this.handleSearchLogs(value);
      }, 300);
    }
  }

  handleExpand(logId) {
    const { expandList } = this.state;
    const newExpandList = expandList.includes(logId)
      ? expandList.filter((id) => id !== logId)
      : [...expandList, logId];
    this.setState({ expandList: newExpandList });
  }

  handleSearchInputChange(e) {
    this.setState({ searchInput: e.target.value });
    this.updateInputValue(e.target.value);
  }

  handleSearchLogs(searchInput) {
    const { changeLogs } = this.state;

    const searchedLogs = _.filter(changeLogs, (log) => {
      const searchStr = searchInput.toLowerCase();
      return (
        log.entityId.toLowerCase().includes(searchStr) ||
        (log.entityName || "").toLowerCase().includes(searchStr) ||
        log.userEmail.toLowerCase().includes(searchStr)
      );
    });

    this.setState({
      searchedLogs,
    });
  }

  handleChartOptions() {
    const { changeLogs, isNetTimezone, selectedRange, dateList } = this.state;

    const timeList = changeLogs.map(
      (log) =>
        (isNetTimezone ? log.changeTimeInTz : log.changeTimeLocal).split(" ")[0]
    );
    const groupedTime = _.mapValues(_.groupBy(timeList), (logs) => logs.length);

    const statusList = _.mapValues(groupedTime, (d, time) =>
      _.mapValues(
        LOG_STATUS_CHECK,
        (s, statusKey) =>
          _.filter(changeLogs, (log) => {
            const logDate = isNetTimezone
              ? log.changeTimeInTz
              : log.changeTimeLocal;
            return logDate.startsWith(time) && log.status.includes(statusKey);
          }).length
      )
    );

    const maxVal = _.max(_.values(groupedTime)) || 0;
    const minVal = _.min(_.values(groupedTime)) || 0;

    function getRadius(value) {
      const min = 0.25;
      const max = 0.35;
      if (maxVal === minVal) return max;
      return ((value - minVal) / (maxVal - minVal)) * (max - min) + min;
    }

    function isGreenLabel(data) {
      return _.reduce(
        ON_STATUS,
        (result, statusKey) => result + data[statusKey],
        0
      );
    }

    function isOrangeLabel(data) {
      return _.reduce(
        OFF_STATUS,
        (result, statusKey) => result + data[statusKey],
        0
      );
    }

    // color HEX code
    function getStatusTextColor(status) {
      switch (status) {
        case LOG_STATUS.ADDED:
        case LOG_STATUS.ACTIVATE:
          return "#16A34A"; // text-green-600 HEX
        case LOG_STATUS.DEACTIVATED:
          return "#EA580C"; // text-orange-600 HEX
        default:
          return "";
      }
    }

    const chartOptions = {
      chart: {
        type: "bubble",
        height: 110,
        spacingTop: 0,
        spacingBottom: 8,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories: dateList,
        lineWidth: 0,
        min: 0,
        max: dateList.length - 1,
        labels: {
          formatter: function () {
            const m = moment(this.value);
            return getDateTimeLabel(m, this.isFirst);
          },
        },
        plotBands: [
          {
            from: -0.5,
            to: 30 - selectedRange - 0.5,
            color: "rgba(200,200,200,0.2)",
          },
        ],
      },
      yAxis: {
        min: -0.5,
        max: 0.5,
        gridLineWidth: 0,
        title: null,
        labels: { enabled: false },
        plotLines: [
          {
            value: 0,
            color: "#ccc",
            width: 1,
          },
        ],
      },
      legend: { enabled: false },
      tooltip: {
        useHTML: true,
        headerFormat: "",
        pointFormatter: function () {
          return `<b>${this.dateLabel}</b><br/>Logs: <b>${this.z}</b>${_.keys(
            statusList[dateList[this.x]]
          )
            .map((key) =>
              statusList[dateList[this.x]][key] > 0
                ? `<br/><span>${key}: <span style="color: ${getStatusTextColor(
                    key
                  )}"><b>${statusList[dateList[this.x]][key]}</b></span></span>`
                : ""
            )
            .join("")}`;
        },
      },
      plotOptions: {
        series: {
          cursor: "pointer",
          minSize: 20,
          maxSize: 34,
          marker: {
            fillOpacity: 0.8,
            lineWidth: 0,
          },
          dataLabels: {
            enabled: true,
            format: "{point.z}",
            y: 0,
            style: { color: "#eee", textOutline: "none", fontSize: "12px" },
          },
          point: {
            events: {
              click: (e) => {
                const date = dateList[e.point.x];
                this.handleClickDateLabel(date);
              },
            },
          },
        },
      },
      series: [
        {
          data: _.keys(groupedTime).map((date) => ({
            x: dateList.indexOf(date),
            y: 0,
            z: groupedTime[date] || 0,
            dateLabel: moment(date).format("YYYY/MM/DD ddd"),
          })),
          color: "#4a90e2",
        },
        {
          data: _.keys(groupedTime).map((date) => ({
            x: dateList.indexOf(date),
            y: isGreenLabel(statusList[date])
              ? getRadius(groupedTime[date])
              : null,
            z: isGreenLabel(statusList[date]) ? 1 : 0,
          })),
          color: "#22C55E",
          dataLabels: {
            enabled: false,
          },
          minSize: 8,
          maxSize: 8,
          marker: {
            fillOpacity: 1,
            lineWidth: 1,
            lineColor: "#E5E7EB",
          },
          enableMouseTracking: false,
        },
        {
          data: _.keys(groupedTime).map((date) => ({
            x: dateList.indexOf(date),
            y: isOrangeLabel(statusList[date])
              ? -getRadius(groupedTime[date])
              : null,
            z: isOrangeLabel(statusList[date]) ? 1 : 0,
          })),
          minSize: 8,
          maxSize: 8,
          color: "#F97316",
          dataLabels: {
            enabled: false,
          },
          marker: {
            fillOpacity: 1,
            lineWidth: 1,
            lineColor: "#E5E7EB",
          },
          enableMouseTracking: false,
        },
      ],
    };

    this.setState({ chartOptions });
  }

  handleClickDateLabel(date) {
    const { rangeOptions, selectedRange } = this.state;
    const includeRange = _.find(rangeOptions, (opt) => {
      return this._checkInRange(date, opt.value);
    });
    if (includeRange.value > selectedRange) {
      this.setState({ selectedRange: includeRange.value }, () => {
        this.handleChartOptions();
        this.handleScrollToDate(date);
      });
    } else {
      this.handleScrollToDate(date);
    }
  }

  handleScrollToDate(date) {
    const element = document.getElementById(date);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  parseChangeType(str) {
    if (str === "modify") return CHANGETYPE.VALUE_CHANGED;
    if (str === "create") return CHANGETYPE.VALUE_ADDED;
    if (str === "delete") return CHANGETYPE.VALUE_REMOVED;
    return "";
  }

  parseChangeToDiff(changes) {
    const keyList = changes.map((change) => change[0]);
    const mapList = _.countBy(keyList);
    const countList = _.mapValues(mapList, (_) => 0);
    return changes.map((change) => {
      const count = mapList[change[0]] > 1 ? ++countList[change[0]] : -1;
      return {
        from: change[2],
        to: change[3],
        type: this.parseChangeType(change[1]),
        path: `${change[0]}${count > 0 ? `[${count}]` : ""}`,
        title: `${change[0]}${count > 0 ? `[${count}]` : ""}`,
      };
    });
  }

  _checkInRange(time, days) {
    const { dateList } = this.state;
    const idx = dateList.indexOf(time.split(" ")[0]);
    if (idx === -1) return false;
    if (!days) return true;
    return idx >= dateList.length - days;
  }

  _getDateList(isNetTimezone = this.state.isNetTimezone) {
    const { logFrom } = this.props;
    const parsedFrom = isNetTimezone
      ? moment.utc(logFrom).tz(this.props.timezone).format("YYYY/MM/DD")
      : moment(logFrom).format("YYYY/MM/DD");

    const parsedTo = isNetTimezone
      ? moment.utc().tz(this.props.timezone).format("YYYY/MM/DD")
      : moment().format("YYYY/MM/DD");

    return getDateList(parsedFrom, parsedTo, true).map((d) =>
      d.replaceAll("-", "/")
    );
  }

  _getStatusBgClass(status) {
    if (_.intersection(ON_STATUS, status).length > 0) {
      return "bg-green-100";
    } else if (_.intersection(OFF_STATUS, status).length > 0) {
      return "bg-orange-100";
    } else {
      return "";
    }
  }

  _getListLengthByStatus(logList, statusKey) {
    if (statusKey === "All") return logList.length;
    return _.filter(logList, (log) => (log.status || []).includes(statusKey))
      .length;
  }

  _getListLengthBySummary(logList, summaryKey) {
    if (summaryKey === "All") return logList.length;
    return _.filter(logList, (log) => (log.summary || []).includes(summaryKey))
      .length;
  }

  render() {
    const { logFrom, title } = this.props;
    const {
      expandList,
      changeLogs,
      searchedLogs,
      selectedSort,
      sortOptions,
      selectedRange,
      rangeOptions,
      isOnlyChanged,
      isNetTimezone,
      searchInput,
      chartOptions,
      selectedKey,
      selectedSummary,
    } = this.state;

    const timeKey = isNetTimezone ? "changeTimeInTz" : "changeTimeLocal";
    const groupedKey = selectedSort.includes("changeTime")
      ? timeKey
      : "entityId";

    // For Layer 1 Filter Options: filter by range
    const rangedChangeLogs = _.filter(changeLogs, (log) => {
      return this._checkInRange(log[timeKey], selectedRange);
    });

    // For Layer 2 Filter Options: filter status key
    const statusLogs =
      selectedKey === "All"
        ? changeLogs
        : _.filter(changeLogs, (log) => log.status.includes(selectedKey));

    // For Layer 2 Filter Options: filter by range and status key
    const rangedStatusLogs =
      selectedKey === "All"
        ? rangedChangeLogs
        : _.filter(rangedChangeLogs, (log) => log.status.includes(selectedKey));

    // Final filtered logs: search input + in range + status key + summary key
    const finalChangeLogs = _.orderBy(
      searchedLogs.filter((log) => {
        const inRange = this._checkInRange(log[timeKey], selectedRange);
        const hasStatus =
          selectedKey === "All" || log.status.includes(selectedKey);
        const hasSummary =
          selectedSummary === "All" || log.summary.includes(selectedSummary);
        return inRange && hasStatus && hasSummary;
      }),
      ...selectedSort.split("-")
    );

    const parsedExpandList = _.filter(expandList, (id) => {
      return _.find(finalChangeLogs, (log) => log.id === id);
    });

    const selectedOption = _.find(sortOptions, (opt) => {
      return opt.value === selectedSort;
    });

    const selectedRangeOption = _.find(rangeOptions, (opt) => {
      return opt.value === selectedRange;
    });

    const parsedFrom = isNetTimezone
      ? moment.utc(logFrom).tz(this.props.timezone).format("YYYY/MM/DD HH:mm Z")
      : moment(logFrom).format("YYYY/MM/DD HH:mm Z");

    const getChangeCount = (changes) =>
      isOnlyChanged
        ? changes.filter((d) => d[1] !== "").length
        : changes.length;

    return (
      <ModalWrapper
        title={{
          label: `${title} Change Log `,
          highlight: `${parsedFrom} ~`,
        }}
        isOpen={true}
        showCloseFooter={true}
        handleClose={this.props.onClose}
        customOverlayStyle={{ zIndex: 100 }}
        width="1140px"
      >
        <div className="mb-1 flex w-full items-center gap-2">
          <div className="mr-auto w-32 text-sm">
            <Select
              value={selectedRangeOption}
              onChange={(opt) =>
                this.setState({ selectedRange: opt.value }, () => {
                  this.handleChartOptions();
                })
              }
              options={rangeOptions}
            />
          </div>

          <div className="flex items-center">
            <label className="rounded flex cursor-pointer items-center gap-1">
              <Checkbox
                isChecked={isOnlyChanged}
                onChange={(val) => {
                  this.setState({ isOnlyChanged: val });
                }}
              ></Checkbox>
              <span className="text-sm font-semibold text-gray-600 hover:text-gray-700">
                Only Show Changed
              </span>
            </label>
          </div>

          <div className="w-56 text-sm">
            <Select
              value={selectedOption}
              onChange={(opt) => this.setState({ selectedSort: opt.value })}
              options={sortOptions}
            />
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-2">
          <label className="rounded flex cursor-pointer items-center gap-1">
            <Checkbox
              isChecked={isNetTimezone}
              onChange={(val) => {
                const dateList = this._getDateList(val);
                this.setState({ isNetTimezone: val, dateList }, () => {
                  this.handleChartOptions();
                });
              }}
            ></Checkbox>
            <span className="text-sm font-semibold text-gray-600 hover:text-gray-700">
              Show in Network Timezone: {this.props.timezone}
            </span>
          </label>
        </div>

        {chartOptions && (
          <HighchartWrapper
            options={chartOptions}
            key={`${isNetTimezone}-${selectedRange}`}
          ></HighchartWrapper>
        )}

        <div className="rounded bg-gray-200 p-2">
          <div className="mb-2 flex flex-wrap items-center gap-1 text-sm">
            <div className="font-semibold text-gray-700">Filter: </div>
            {["All", ..._.keys(LOG_STATUS_CHECK)].map((statusKey) => {
              return (
                <FilterButton
                  key={statusKey}
                  label={statusKey}
                  isSelected={statusKey === selectedKey}
                  count={this._getListLengthByStatus(changeLogs, statusKey)}
                  filtered={
                    selectedRange !== 30
                      ? this._getListLengthByStatus(rangedChangeLogs, statusKey)
                      : null
                  }
                  onClick={() => {
                    this.setState({
                      selectedKey: statusKey,
                      selectedSummary: "All",
                    });
                  }}
                ></FilterButton>
              );
            })}
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-1 text-sm">
            <div className="font-semibold text-gray-700">Change Type: </div>
            {["All", ..._.keys(CHANGE_SUMMARY_CHECK)].map((summary) => {
              return (
                <FilterButton
                  key={summary}
                  label={summary}
                  isSelected={summary === selectedSummary}
                  count={this._getListLengthBySummary(statusLogs, summary)}
                  filtered={
                    selectedRange !== 30
                      ? this._getListLengthBySummary(rangedStatusLogs, summary)
                      : null
                  }
                  onClick={() => {
                    this.setState({ selectedSummary: summary });
                  }}
                ></FilterButton>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2/5">
              <input
                type="text"
                className={inputClass}
                placeholder="Search id or name or user email"
                value={searchInput}
                onChange={this.handleSearchInputChange}
              />
            </div>
            {searchInput !== "" && (
              <div className="text-gray-600">
                {`${finalChangeLogs.length} of `}
                {this._getListLengthBySummary(statusLogs, selectedSummary)}{" "}
                items
              </div>
            )}
            <div className="ml-auto">
              <button
                type="button"
                className={buttonActionClassGray}
                onClick={() =>
                  this.setState({
                    expandList:
                      parsedExpandList.length > 0
                        ? []
                        : finalChangeLogs.map((log) => log.id),
                  })
                }
              >
                {parsedExpandList.length > 0
                  ? `Collapse All (${parsedExpandList.length})`
                  : "Expand All"}
              </button>
            </div>
          </div>
        </div>

        <div
          className="border-t mt-2 flex flex-col gap-3 overflow-y-auto border-gray-400 pl-1 pr-4 pt-2"
          style={{ height: "420px" }}
        >
          {finalChangeLogs.length === 0 && (
            <div className="text-center text-gray-600">
              No change log found.
            </div>
          )}
          {finalChangeLogs.map((log, idx) => {
            return (
              <React.Fragment key={log.id}>
                {(idx === 0 ||
                  finalChangeLogs[idx - 1][groupedKey].split(" ")[0] !==
                    log[groupedKey].split(" ")[0]) && (
                  <div
                    className={`bg-gray-700 py-1 text-center font-semibold text-white ${
                      idx !== 0 ? "mt-3" : ""
                    }`}
                    id={log[groupedKey].split(" ")[0]}
                  >
                    {log[groupedKey].split(" ")[0]}
                  </div>
                )}
                <div
                  className={`hover:shadow group relative border-t-2 border-gray-500 ${this._getStatusBgClass(
                    log.status
                  )} py-2 pl-4 pr-6 hover:border-gray-600`}
                >
                  <div className="flex w-full items-start gap-2 text-sm">
                    <Item
                      label="Updated At"
                      value={
                        <div className="text-base font-semibold text-blue-600">
                          {log[timeKey]}
                        </div>
                      }
                    />
                    <DataLabel list={log.status}></DataLabel>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <div className="w-1/2">
                      <Item
                        label="Id/Name"
                        value={`${log.entityId} - ${log.entityName || "--"}`}
                      />
                    </div>
                    <div className="w-1/2">
                      <Item label="User" value={log.userEmail} />
                    </div>
                  </div>
                  {expandList.includes(log.id) ? (
                    <div className="flex w-full flex-col">
                      {log.changeDiff.length > 0 && (
                        <>
                          <div className="text-sm text-gray-600">
                            Main Rule Changes
                          </div>
                          <ConfigDiffView
                            diffs={
                              isOnlyChanged
                                ? log.changeDiff.filter((d) => d.type !== "")
                                : log.changeDiff
                            }
                            height="auto"
                          ></ConfigDiffView>
                        </>
                      )}

                      {log.subChangeDiff.length > 0 && (
                        <>
                          <div className="text-sm text-gray-600">
                            Sub Rules Changes
                          </div>
                          {log.subChangeDiff.map((subRule) => (
                            <div key={`${log.id}-${subRule.entityId}`}>
                              <ConfigDiffView
                                title={`${subRule.entityId} -`}
                                diffs={
                                  isOnlyChanged
                                    ? subRule.changes.filter(
                                        (d) => d.type !== ""
                                      )
                                    : subRule.changes
                                }
                                height="auto"
                              ></ConfigDiffView>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex w-full items-baseline gap-2 text-sm">
                      <div className="flex items-center gap-1">
                        <Item
                          label="Changes"
                          highlight={true}
                          value={
                            <div className="flex items-center gap-2">
                              <div className="text-base font-bold">
                                {getChangeCount(log.changes)}
                              </div>
                              {log.subRules && log.subRules.length > 0 && (
                                <div>(Sub rules: {log.subRules.length})</div>
                              )}
                            </div>
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <DataLabel list={log.summary}></DataLabel>
                      </div>
                    </div>
                  )}

                  <div
                    className={`${buttonActionClassGray} absolute ml-auto hidden cursor-pointer group-hover:block`}
                    style={{ top: "0.75rem", right: "0.75rem" }}
                    onClick={() => this.handleExpand(log.id)}
                  >
                    <div className="flex items-center gap-1">
                      {expandList.includes(log.id) ? (
                        <>
                          Collapse<IoIosArrowUp></IoIosArrowUp>
                        </>
                      ) : (
                        <>
                          Expand<IoIosArrowDown></IoIosArrowDown>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </ModalWrapper>
    );
  }
}

class Item extends React.Component {
  render() {
    const { label, value, width = "", highlight = false } = this.props;

    return (
      <div className="mb-1 flex items-center gap-1">
        <span className={`${width === "" ? "w-20" : width} text-gray-600`}>
          {label}
        </span>
        <div
          className={`flex-1 break-all font-medium ${
            highlight ? "text-blue-600" : "text-gray-800"
          }`}
        >
          {value}
        </div>
      </div>
    );
  }
}

const CHANGE_SUMMARY_VALUES = _.values(CHANGE_SUMMARY);
const parsedClass = (item) => {
  if (ON_STATUS.includes(item)) {
    return "bg-green-500 text-green-100";
  } else if (OFF_STATUS.includes(item)) {
    return "bg-orange-500 text-orange-100";
  } else if (CHANGE_SUMMARY_VALUES.includes(item)) {
    return "text-blue-600 border border-blue-600";
  } else {
    return "";
  }
};

function DataLabel({ list }) {
  if (list.length === 0) return null;
  return list.map((item) => (
    <div className={`rounded px-2 font-bold ${parsedClass(item)}`} key={item}>
      {item}
    </div>
  ));
}

function FilterButton({ label, count, filtered, isSelected, onClick }) {
  return count === 0 ? (
    <button
      type="button"
      disabled
      className="border rounded cursor-not-allowed border-gray-200 bg-gray-100 px-2 py-1 font-medium text-gray-500"
    >
      {label}
    </button>
  ) : (
    <button
      type="button"
      className={`border rounded border-gray-400 font-medium ${
        isSelected ? "bg-blue-200" : "bg-white"
      }  px-2 py-1 text-gray-800 hover:border-blue-400`}
      onClick={onClick}
    >
      {label} ({_.isNil(filtered) ? "" : `${filtered} / `}
      {count})
    </button>
  );
}

export default ChangeLogModal;
